import React, { FunctionComponent, useCallback, useEffect, useMemo } from "react";

import { DataGrid, DataGridProps, DataGridVariant } from "@multiweb/reactComponents";
import { createSkeletonRowsKeys, EMPTY_ARRAY, invariant, Key, keyGenerator } from "@multiweb/reactUtils";
import { FetchingState, RowRenderer } from "@multiweb/types/react";
import { EmptyMessage } from "../../../components/EmptyMessage";
import { GRID_PARTICIPANTS_ROLE_USERS_TEMPLATE_RENDERER_KEY } from "../../constants";
import { GridParticipantsItem } from "../GridParticipantsItem";
import { classNames, DEFAULT_DATA_COUNT_TO_LOAD } from "./constants";
import { calculateEstimateOfRowHeight } from "./utils";

export type GridParticipantsComponentProps = {
    /** Unikátní klíč pro DataSource. */
    dataSourceKey: string;
    /** Funkce, která zajistí načtení dat. */
    loadData: () => void;
    /** Seřazené identifikátory dat z DataSource. */
    recordsIds?: ReadonlyArray<string>;
    /** Zobrazit "podřádky" uživatelů rolí? */
    expandRoleUsers: boolean;
    /** Uchovává informaci, v jakém stavu je načítání dat. */
    fetchingState?: FetchingState;
    /** Počet přichozích dat */
    fetchingCount?: number;
    /** Počet dětí záznamu, využívá se pro výpočet odhadu výšky záznamu. */
    rootNodesChildrenCount?: number;
    /** Zaskroluje na daný index. Použito ve VirtualizedListu. */
    scrollToIndex?: number;
    /** Akce spouštěná po stisknutí klávesy Enter */
    onEnterPress?: () => void;
} & Pick<DataGridProps<string>, "onScroll">;

export const GridParticipantsComponent: FunctionComponent<GridParticipantsComponentProps> = ({
    dataSourceKey,
    recordsIds = EMPTY_ARRAY,
    expandRoleUsers,
    fetchingState = FetchingState.initialized,
    fetchingCount = 0,
    rootNodesChildrenCount = 0,
    scrollToIndex,
    loadData,
    onEnterPress,
    onScroll,
}) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === Key.Enter) {
            onEnterPress?.();
        }
    };

    const itemRenderer: RowRenderer<string> = useCallback(
        (recordId, index, items) => {
            invariant(recordId, "[GridParticipantsComponent] At least one record has not correct id attribute.");
            const isFirst = index === 0 || undefined;
            const isLast = (index === (items?.length ?? 0) - 1) || undefined;

            return (
                <GridParticipantsItem
                    templateKey={GRID_PARTICIPANTS_ROLE_USERS_TEMPLATE_RENDERER_KEY}
                    {...{ dataSourceKey, recordId, isFirst, isLast, expandRoleUsers }}
                />

            );
        },
        [expandRoleUsers, dataSourceKey],
    );

    const noRowsRenderer = useCallback(() => <EmptyMessage dataSourceKey={dataSourceKey} />, [dataSourceKey]);

    useEffect(() => {
        if (fetchingState === FetchingState.initialized) {
            loadData();
        }
    }, [ fetchingState, loadData ]);

    const estimatedRowSize = useMemo(() => calculateEstimateOfRowHeight(recordsIds.length, rootNodesChildrenCount), [recordsIds, rootNodesChildrenCount]);

    return (
        <div className={classNames()} onKeyDown={handleKeyDown}>
            <DataGrid
                items={recordsIds}
                loadOnScroll={true}
                canLoadData={false}
                dataCountToLoad={DEFAULT_DATA_COUNT_TO_LOAD}
                loadData={() => null}
                variant={DataGridVariant.gridParticipants}
                {...{ keyGenerator, fetchingState, createSkeletonRowsKeys, noRowsRenderer, itemRenderer, fetchingCount, estimatedRowSize, scrollToIndex, onScroll }}
            />
        </div>
    );
};
